import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import Button from 'src/components-new/primitives/Button'
import {ITALIAN_REGIONS} from 'src/constants/mock/county'
import {ITALIAN_PROVINCE} from 'src/constants/mock/province'
import {ITALIAN_CITY} from 'src/constants/mock/city'
import {CustomerUserRow} from './customerUsersColumns'

const SEX_OPTIONS = [
  {value: 'MALE', label: 'Maschio'},
  {value: 'FEMALE', label: 'Femmina'},
  {value: 'NOT_DECLARED', label: 'Non dichiarato'},
]

const PREFERRED_CONTACT_OPTIONS = [
  {value: 'EMAIL', label: 'Email'},
  {value: 'SMS', label: 'SMS'},
  {value: 'PUSH', label: 'Push'},
]

interface CustomerUserFormState {
  title: string
  lastName: string
  firstName: string
  sex: string
  nationality: string
  phoneNumber: string
  cellPhoneNumber: string
  faxNumber: string
  email: string
  skypeId: string
  preferredContacts: string[]
  address: string
  streetNumber: string
  postalCode: string
  locality: string
  country: string
  region: string
  province: string
  city: string
}

function buildFormState(customerUser: CustomerUserRow): CustomerUserFormState {
  return {
    title: customerUser.title ?? '',
    lastName: customerUser.lastName ?? '',
    firstName: customerUser.firstName ?? '',
    sex: customerUser.sex ?? '',
    nationality: customerUser.nationality ?? '',
    phoneNumber: customerUser.phoneNumber ?? '',
    cellPhoneNumber: customerUser.cellPhoneNumber ?? '',
    faxNumber: customerUser.faxNumber ?? '',
    email: customerUser.email ?? '',
    skypeId: customerUser.skypeId ?? '',
    preferredContacts: customerUser.preferredContacts ?? [],
    address: customerUser.address?.address ?? '',
    streetNumber: customerUser.address?.streetNumber ?? '',
    postalCode: customerUser.address?.postalCode ?? '',
    locality: customerUser.address?.locality ?? '',
    country: customerUser.address?.country ?? 'Italia',
    region: customerUser.address?.region ?? '',
    province: customerUser.address?.province ?? '',
    city: customerUser.address?.city ?? '',
  }
}

export interface CustomerUserDetailDialogNewProps {
  customerUser: CustomerUserRow
  readOnly: boolean
  open: boolean
  onClose: () => void
  onSave: (id: string, body: any) => Promise<void>
}

// Same flat, unsectioned 2-column grid as PatientEventDialogNew's body — no Card
// wrapping/section titles, just fields directly under the AppDialog title divider.
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 2,
  pt: 1,
} as const

/**
 * New-style equivalent of the legacy CustomerUserDialog (src/components/templates/
 * CustomerUserDialog/CustomerUserDialog.tsx). Unlike MedicalCenterDetailDialogNew this
 * dialog is not purely read-only: a customer user can edit their OWN record (readOnly
 * is computed by the caller by comparing customerUser.authUserId with the logged-in
 * user's authUserId), while any other record stays view-only.
 *
 * Every field is always rendered as its normal editable widget and simply gets
 * `disabled={readOnly}` — same approach as the legacy dialog (`disabled={readOnly}` on
 * every antd Input/Select) and as VirtualMeetingDetailDialogNew, instead of swapping in a
 * separate ReadOnlyField component per field.
 *
 * Unlike the list's columns (customerUsersColumns.tsx), which are PSP-driven via
 * `customerUtilitiesConfigurations.columnOptions`, this dialog is NOT filtered by that
 * config — the detail view always shows every field, regardless of which columns the PSP
 * configured to show in the table.
 */
export default function CustomerUserDetailDialogNew({
  customerUser,
  readOnly,
  open,
  onClose,
  onSave,
}: CustomerUserDetailDialogNewProps): JSX.Element {
  const toast = useToast()
  const [form, setForm] = useState<CustomerUserFormState>(() => buildFormState(customerUser))
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(buildFormState(customerUser))
      setSubmitted(false)
    }
  }, [customerUser, open])

  const setField = <K extends keyof CustomerUserFormState>(key: K, value: CustomerUserFormState[K]) => {
    setForm(prev => ({...prev, [key]: value}))
  }

  const handleSave = async () => {
    setSubmitted(true)
    if (!canSave) {
      return
    }
    setSaving(true)
    try {
      await onSave(customerUser._id, {
        title: form.title,
        lastName: form.lastName,
        firstName: form.firstName,
        sex: form.sex,
        nationality: form.nationality,
        phoneNumber: form.phoneNumber,
        cellPhoneNumber: form.cellPhoneNumber,
        faxNumber: form.faxNumber,
        email: form.email,
        skypeId: form.skypeId,
        preferredContacts: form.preferredContacts.length > 0 ? form.preferredContacts : undefined,
        address: {
          address: form.address,
          city: form.city,
          province: form.province,
          region: form.region,
          postalCode: form.postalCode,
          streetNumber: form.streetNumber,
          country: form.country,
          locality: form.locality,
        },
      })
      onClose()
    } catch {
      toast.error('Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const dialogTitle = `${form.lastName} ${form.firstName}`.trim() || 'Utenza cliente'
  const lastNameError = submitted && form.lastName.trim().length === 0
  const firstNameError = submitted && form.firstName.trim().length === 0
  const canSave = !saving && form.lastName.trim().length > 0 && form.firstName.trim().length > 0

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth="md"
      actions={!readOnly ? (
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{'Annulla'}</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>{'Salva'}</Button>
        </>
      ) : undefined}
    >
      <Box sx={GRID_SX}>
        <TextFieldNew label="Titolo" value={form.title} onChange={v => setField('title', v)} disabled={readOnly} fullWidth />
        <TextFieldNew
          label="Cognome"
          value={form.lastName}
          onChange={v => setField('lastName', v)}
          disabled={readOnly}
          required
          fullWidth
          error={lastNameError}
          helperText={lastNameError ? 'Campo obbligatorio' : undefined}
        />
        <TextFieldNew
          label="Nome"
          value={form.firstName}
          onChange={v => setField('firstName', v)}
          disabled={readOnly}
          required
          fullWidth
          error={firstNameError}
          helperText={firstNameError ? 'Campo obbligatorio' : undefined}
        />
        <SelectNew label="Sesso" value={form.sex} onChange={v => setField('sex', v)} options={SEX_OPTIONS} disabled={readOnly} fullWidth />
        <TextFieldNew label="Nazionalità" value={form.nationality} onChange={v => setField('nationality', v)} disabled={readOnly} fullWidth />

        <CheckboxGroupNew
          sx={{gridColumn: '1 / -1'}}
          label="Metodi di comunicazione preferiti"
          value={form.preferredContacts}
          onChange={v => setField('preferredContacts', v)}
          options={PREFERRED_CONTACT_OPTIONS}
          disabled={readOnly}
        />
        <TextFieldNew label="Telefono" type="tel" value={form.phoneNumber} onChange={v => setField('phoneNumber', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Cellulare" type="tel" value={form.cellPhoneNumber} onChange={v => setField('cellPhoneNumber', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Fax" type="tel" value={form.faxNumber} onChange={v => setField('faxNumber', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Email" type="email" value={form.email} onChange={v => setField('email', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Contatto Skype" value={form.skypeId} onChange={v => setField('skypeId', v)} disabled={readOnly} fullWidth />

        <TextFieldNew label="Stato" value={form.country} onChange={() => undefined} disabled fullWidth />
        <SelectNew label="Regione" value={form.region} onChange={v => setField('region', v)} options={ITALIAN_REGIONS} disabled={readOnly} showSearch fullWidth />
        <SelectNew label="Provincia" value={form.province} onChange={v => setField('province', v)} options={ITALIAN_PROVINCE} disabled={readOnly} showSearch fullWidth />
        <SelectNew label="Città" value={form.city} onChange={v => setField('city', v)} options={ITALIAN_CITY} disabled={readOnly} showSearch fullWidth />
        <TextFieldNew label="Indirizzo" value={form.address} onChange={v => setField('address', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Civico" value={form.streetNumber} onChange={v => setField('streetNumber', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Codice Postale" value={form.postalCode} onChange={v => setField('postalCode', v)} disabled={readOnly} fullWidth />
        <TextFieldNew label="Località" value={form.locality} onChange={v => setField('locality', v)} disabled={readOnly} fullWidth />
      </Box>
    </AppDialog>
  )
}
